import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { api } from '../services/api';
import { usePrices } from '../context/PriceContext';
import { Image } from '../types';
import ImageGallery from '../components/gallery/ImageGallery';
import ImageViewer from '../components/gallery/ImageViewer';

const PreviewContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  color: ${props => props.theme.colors.text.primary};
  font-size: 18px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 50vh;
  color: ${props => props.theme.colors.text.primary};
  text-align: center;
  gap: 16px;
`;

const ErrorTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  color: ${props => props.theme.colors.text.primary};
`;

const ErrorText = styled.p`
  margin: 0;
  font-size: 16px;
  color: ${props => props.theme.colors.text.secondary};
  max-width: 400px;
  line-height: 1.5;
`;

const BackButton = styled.button`
  padding: 12px 24px;
  background-color: ${props => props.theme.colors.button.primary.background};
  color: ${props => props.theme.colors.button.primary.text};
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.colors.button.primary.hover};
  }
`;

const PreviewContentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getPriceByType } = usePrices();
  
  const [image, setImage] = useState<Image | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showImageViewer, setShowImageViewer] = useState(false);

  // Obtener el ID de la imagen desde los parámetros de la URL
  const rsrc = searchParams.get('rsrc');

  useEffect(() => {
    const loadSharedImage = async () => {
      if (!rsrc) {
        setError('No se especificó ninguna imagen para mostrar');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('Loading shared image with ID:', rsrc);
        const individualPrice = getPriceByType('individual');
        const fetchedImage = await api.getImageById(rsrc, individualPrice);
        
        if (fetchedImage) {
          setImage(fetchedImage);
          // Mostrar automáticamente el ImageViewer después de un pequeño delay
          setTimeout(() => {
            setShowImageViewer(true);
          }, 500);
        } else {
          setError('La imagen solicitada no fue encontrada o no está disponible');
        }
      } catch (err) {
        console.error('Error loading shared image:', err);
        setError('Ocurrió un error al cargar la imagen');
      } finally {
        setLoading(false);
      }
    };

    loadSharedImage();
  }, [rsrc, getPriceByType]);

  const handleCloseImageViewer = () => {
    setShowImageViewer(false);
    // Opcional: redirigir a la página principal después de cerrar
    setTimeout(() => {
      navigate('/');
    }, 300);
  };

  const handleBackToGallery = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <PreviewContainer>
        <LoadingContainer>
          Cargando imagen compartida...
        </LoadingContainer>
      </PreviewContainer>
    );
  }

  if (error || !image) {
    return (
      <PreviewContainer>
        <ErrorContainer>
          <ErrorTitle>Imagen no encontrada</ErrorTitle>
          <ErrorText>
            {error || 'La imagen que estás buscando no existe o ha sido eliminada.'}
          </ErrorText>
          <BackButton onClick={handleBackToGallery}>
            Explorar Galería
          </BackButton>
        </ErrorContainer>
      </PreviewContainer>
    );
  }

  return (
    <PreviewContainer>
      {/* Mostrar la galería normal de fondo */}
      <ImageGallery />
      
      {/* Mostrar el ImageViewer automáticamente si la imagen se cargó */}
      {showImageViewer && image && (
        <ImageViewer 
          image={image} 
          onClose={handleCloseImageViewer}
        />
      )}
    </PreviewContainer>
  );
};

export default PreviewContentPage;